import { getDeviceId } from '../deviceUtils';
import { ClientReportStore } from './ClientReportStore';
import { ClientOperationReportRecord, ClientReportOpType } from './clientReportTypes';
import { hash32, safeJsonStringify, sanitizeForClientReport, sanitizeAndTruncateForClientReport, CLIENT_REPORT_MAX_LEN_MAP, truncateToLength, getClientIp } from './clientReportUtils';
import { reportClientOperationBatch } from '../../graphql/mutations/clientReport';

export interface ClientReportBatchResult {
  acceptedKeys: string[];
  failedKeys: string[];
  switchValue: number;
}

export type ClientReportPoller = () => Promise<ClientReportBatchResult | null>;

export class ClientReporter {
  private store: ClientReportStore;
  private switchCounter: number;
  private switchStorageKey: string;
  private started: boolean;
  private timerId: number | null;
  private opType: string;

  constructor(opType: string, prefix: string) {
    this.opType = opType;
    this.store = new ClientReportStore(prefix);
    this.switchStorageKey = `client_report.${opType}.switch_counter`;
    this.switchCounter = this.loadSwitchCounter();
    this.started = false;
    this.timerId = null;
  }

  createReportKey(barCode: string): string {
    const cleanBarCode = sanitizeForClientReport(barCode);
    const deviceId = (() => {
      try {
        return getDeviceId();
      } catch (e) {
        return 'device_unknown';
      }
    })();
    const now = Date.now().toString(36);
    const rnd = Math.random().toString(36).slice(2, 10);
    const seed = `${cleanBarCode}|${deviceId}|${now}|${rnd}`;
    // 使用运单号作为前缀，确保同一运单号的操作有相同前缀；按表字段最大长度截取
    const raw = `${cleanBarCode}_${hash32(seed)}_${deviceId}_${now}_${rnd}`;
    return truncateToLength(raw, CLIENT_REPORT_MAX_LEN_MAP.reportKey);
  }

  async onScanTriggered(reportKey: string, barCode: string): Promise<void> {
    const cleanReportKey = sanitizeForClientReport(reportKey);
    const cleanBarCode = sanitizeForClientReport(barCode);
    const deviceId = (() => {
      try {
        return getDeviceId();
      } catch (e) {
        return 'device_unknown';
      }
    })();
    const clientIp = getClientIp();
    await this.store.upsert(cleanReportKey, this.opType as ClientReportOpType, {
      barCode: cleanBarCode,
      deviceId,
      clientIp,
      touchAtMs: Date.now()
    });
  }

  async onIntercepted(reportKey: string, reason: string): Promise<void> {
    const cleanReportKey = sanitizeForClientReport(reportKey);
    await this.store.upsert(cleanReportKey, this.opType as ClientReportOpType, {
      interceptAtMs: Date.now(),
      interceptReason: truncateToLength(reason, 300)
    });
  }

  async onRequest(reportKey: string, params: unknown): Promise<void> {
    const maybeBarCode = (() => {
      if (!params || typeof params !== 'object') return undefined;
      const v = (params as { barCode?: unknown }).barCode;
      if (typeof v !== 'string') return undefined;
      const cleaned = sanitizeForClientReport(v);
      return cleaned.length > 0 ? cleaned : undefined;
    })();
    const cleanReportKey = sanitizeForClientReport(reportKey);

    // 收集客户端请求头
    const requestHeaders: Record<string, string> = {};
    if (typeof navigator !== 'undefined') {
      if (navigator.userAgent) {
        requestHeaders['User-Agent'] = navigator.userAgent;
      }
      if (navigator.language) {
        requestHeaders['Accept-Language'] = navigator.language;
      }
      if (navigator.languages && navigator.languages.length > 0) {
        requestHeaders['Accept-Language'] = navigator.languages.join(',');
      }
    }

    await this.store.upsert(cleanReportKey, this.opType as ClientReportOpType, {
      barCode: maybeBarCode,
      requestAtMs: Date.now(),
      requestParamsJson: safeJsonStringify(params),
      requestHeadersJson: safeJsonStringify(requestHeaders)
    });
  }

  async onResponse(reportKey: string, result: unknown): Promise<void> {
    const cleanReportKey = sanitizeForClientReport(reportKey);
    await this.store.upsert(cleanReportKey, this.opType as ClientReportOpType, {
      responseAtMs: Date.now(),
      responseResult: truncateToLength(safeJsonStringify(result), 300)
    });
  }

  getSwitchCounter(): number {
    return this.switchCounter;
  }

  setSwitchCounter(value: number): void {
    this.switchCounter = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
    this.saveSwitchCounter();
  }

  tickSwitchCounter(): void {
    if (this.switchCounter > 0) {
      this.switchCounter -= 1;
      this.saveSwitchCounter();
    }
  }

  async listReadyBatch(limit: number, minAgeMs: number): Promise<ClientOperationReportRecord[]> {
    return this.store.listReady(limit, minAgeMs);
  }

  async deleteKeys(keys: string[]): Promise<void> {
    await this.store.removeMany(keys);
  }

  /**
   * 直接上报（绕过 IndexedDB，用于 IndexedDB 写入失败时的兜底方案）
   */
  async reportDirectly(reportKey: string, responseData: { responseAtMs: number; responseResult: string }): Promise<void> {
    try {
      const cleanReportKey = sanitizeForClientReport(reportKey);
      if (!cleanReportKey) return;
      // 尝试从 IndexedDB 读取现有记录
      const existing = await this.store.getItem(cleanReportKey);
      const record: ClientOperationReportRecord = existing || {
        reportKey: cleanReportKey,
        opType: this.opType as ClientReportOpType,
        barCode: '',
        createdAtMs: Date.now(),
        updatedAtMs: Date.now()
      };

      // 合并响应数据
      const merged: ClientOperationReportRecord = {
        ...record,
        ...responseData
      };

      // 构建上报项（清洗换行/回车并按表字段最大长度截断，避免服务端入库异常）
      const item: {
        reportKey: string;
        opType: string;
        barCode: string;
        deviceId?: string;
        clientIp?: string;
        createdAtMs?: number;
        touchAtMs?: number;
        interceptAtMs?: number;
        interceptReason?: string;
        requestAtMs?: number;
        requestParamsJson?: string;
        requestHeadersJson?: string;
        responseAtMs?: number;
        responseResult?: string;
      } = {
        reportKey: sanitizeAndTruncateForClientReport(merged.reportKey, CLIENT_REPORT_MAX_LEN_MAP.reportKey),
        opType: sanitizeAndTruncateForClientReport(merged.opType, CLIENT_REPORT_MAX_LEN_MAP.opType),
        barCode: sanitizeAndTruncateForClientReport(merged.barCode || '', CLIENT_REPORT_MAX_LEN_MAP.barCode),
        deviceId: merged.deviceId,
        clientIp: merged.clientIp,
        createdAtMs: merged.createdAtMs,
        touchAtMs: merged.touchAtMs,
        interceptAtMs: merged.interceptAtMs,
        interceptReason: merged.interceptReason,
        requestAtMs: merged.requestAtMs,
        requestParamsJson: merged.requestParamsJson,
        requestHeadersJson: merged.requestHeadersJson,
        responseAtMs: merged.responseAtMs,
        responseResult: merged.responseResult
      };

      // 直接上报
      await reportClientOperationBatch({
        opType: this.opType,
        items: [item]
      });
    } catch (error) {
      // 直接上报也失败，静默处理（避免影响主流程）
    }
  }

  startPolling(poller: ClientReportPoller): void {
    if (this.started) return;
    this.started = true;

    const runOnce = async () => {
      try {
        // 每次轮询先递减开关值
        this.tickSwitchCounter();
        // 然后执行轮询逻辑（poller 中会检查减1后的开关值）
        const result = await poller();
        if (!result) return;
        // 如果后端返回了新的开关值，覆盖当前值
        if (result.switchValue !== 0) {
          this.setSwitchCounter(result.switchValue);
        }
        // 删除成功上报的记录
        if (Array.isArray(result.acceptedKeys) && result.acceptedKeys.length > 0) {
          await this.deleteKeys(result.acceptedKeys);
        }
      } catch (e) {
        console.error('[ClientReporter] Polling error:', e);
        return;
      }
    };

    void runOnce();
    this.timerId = window.setInterval(() => {
      void runOnce();
    }, 60 * 1000);
  }

  stopPolling(): void {
    if (this.timerId !== null) {
      window.clearInterval(this.timerId);
      this.timerId = null;
    }
    this.started = false;
  }

  private loadSwitchCounter(): number {
    try {
      const raw = localStorage.getItem(this.switchStorageKey);
      const value = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(value) ? Math.max(0, value) : 0;
    } catch (e) {
      return 0;
    }
  }

  private saveSwitchCounter(): void {
    try {
      localStorage.setItem(this.switchStorageKey, String(this.switchCounter));
    } catch (e) {
      return;
    }
  }
}

